import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { databaseService } from '@/services/database/DatabaseService';
import type { FieldSyncOutboxItem } from '@/mobile/sync/fieldSyncCache';

const LEGACY_OUTBOX_KEY = 'engetel_field_sync_outbox_v1';
const MIGRATED_META = 'field_sync_outbox_sqlite_migrated_v1';

let migrationPromise: Promise<void> | null = null;

function parseOperationJson(raw: string): FieldSyncOutboxItem['operation'] | null {
  try {
    return JSON.parse(raw) as FieldSyncOutboxItem['operation'];
  } catch {
    return null;
  }
}

async function ensureMigratedFromPreferences(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  if (!migrationPromise) {
    migrationPromise = (async () => {
      const migrated = await databaseService.getMeta(MIGRATED_META);
      if (migrated === '1') {
        return;
      }

      const { value } = await Preferences.get({ key: LEGACY_OUTBOX_KEY });
      if (value) {
        try {
          const items = JSON.parse(value) as FieldSyncOutboxItem[];
          for (const item of items) {
            await databaseService.runQuery(
              `INSERT OR REPLACE INTO field_sync_outbox
               (id, created_at, last_push_error, last_push_at, operation_json)
               VALUES (?, ?, ?, ?, ?)`,
              [
                item.id,
                item.createdAt,
                item.lastPushError ?? null,
                item.lastPushAt ?? null,
                JSON.stringify(item.operation),
              ],
            );
          }
        } catch (error) {
          console.warn('[field-sync-outbox] migração Preferences falhou', error);
        }
      }

      await Preferences.remove({ key: LEGACY_OUTBOX_KEY });
      await databaseService.setMeta(MIGRATED_META, '1');
    })();
  }

  await migrationPromise;
}

function rowToItem(row: Record<string, unknown>): FieldSyncOutboxItem | null {
  const operation = parseOperationJson(String(row.operation_json ?? ''));
  if (!operation) {
    return null;
  }

  return {
    id: String(row.id),
    createdAt: String(row.created_at),
    lastPushError: row.last_push_error ? String(row.last_push_error) : null,
    lastPushAt: row.last_push_at ? String(row.last_push_at) : null,
    operation,
  };
}

export async function loadOutboxFromSqlite(): Promise<FieldSyncOutboxItem[]> {
  if (!Capacitor.isNativePlatform()) {
    return [];
  }

  await ensureMigratedFromPreferences();

  const rows = await databaseService.queryRows<Record<string, unknown>>(
    `SELECT id, created_at, last_push_error, last_push_at, operation_json
     FROM field_sync_outbox
     ORDER BY created_at ASC`,
  );

  return rows.map(rowToItem).filter((item): item is FieldSyncOutboxItem => item !== null);
}

export async function saveOutboxToSqlite(items: FieldSyncOutboxItem[]): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await ensureMigratedFromPreferences();
  await databaseService.runQuery(`DELETE FROM field_sync_outbox`);

  for (const item of items) {
    await databaseService.runQuery(
      `INSERT INTO field_sync_outbox
       (id, created_at, last_push_error, last_push_at, operation_json)
       VALUES (?, ?, ?, ?, ?)`,
      [
        item.id,
        item.createdAt,
        item.lastPushError ?? null,
        item.lastPushAt ?? null,
        JSON.stringify(item.operation),
      ],
    );
  }
}

export async function appendOutboxItemToSqlite(item: FieldSyncOutboxItem): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  await ensureMigratedFromPreferences();
  await databaseService.runQuery(
    `INSERT INTO field_sync_outbox
     (id, created_at, last_push_error, last_push_at, operation_json)
     VALUES (?, ?, ?, ?, ?)`,
    [
      item.id,
      item.createdAt,
      item.lastPushError ?? null,
      item.lastPushAt ?? null,
      JSON.stringify(item.operation),
    ],
  );
}
